import type { ComponentType } from "react";
import { use, useEffect, useRef, useState } from "react";
import {
  LuChartBarBig as LuBarChartHorizontalBig,
  LuFileText,
  LuLink,
  LuLock,
  LuSmilePlus,
} from "react-icons/lu";

import { EditorActionAudio } from "@/components/app/editor-v3/actions/editor-action-audio";
import { EditorActionDraft } from "@/components/app/editor-v3/actions/editor-action-draft";
import { EditorActionEmbed } from "@/components/app/editor-v3/actions/editor-action-embed";
import { EditorActionEmoji } from "@/components/app/editor-v3/actions/editor-action-emoji";
import { EditorActionGated } from "@/components/app/editor-v3/actions/editor-action-gated";
import { EditorActionGif } from "@/components/app/editor-v3/actions/editor-action-gif";
import { EditorActionPoll } from "@/components/app/editor-v3/actions/editor-action-poll";
import { EditorActionSubmit } from "@/components/app/editor-v3/actions/editor-action-submit";
import { EditorActionSwapGatedContent } from "@/components/app/editor-v3/actions/editor-action-swap-gated-content";
import { EditorActionVideo } from "@/components/app/editor-v3/actions/editor-action-video";
import {
  EDITOR_MEDIA_TAB,
  EDITOR_MODE,
} from "@/components/app/editor-v3/constants";
import { saveDraft } from "@/components/app/editor-v3/draft-utils";
import { EditorEmbedActionDetails } from "@/components/app/editor-v3/editor-embed-action-details";
import { EditorEmojiPicker } from "@/components/app/editor-v3/editor-emoji-picker";
import { EditorPollForm } from "@/components/app/editor-v3/editor-poll-form";
import { HorizontalScrollRail } from "@/components/shared/horizontal-scroll-rail";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserContext } from "@/contexts/user-context";
import { formatUSDCents } from "@/utils/currency";
import { cn } from "@/utils/lib";
import { AccessGroupType } from "@/utils/subscriptions";

import { EditorActionFormatting } from "../actions/editor-action-formatting";
import { EditorActionPhoto } from "../actions/editor-action-photo";
import {
  EditorFooterActionButton,
  EditorFooterActionContainer,
} from "../editor-action-utils";
import {
  EditorFieldContextProvider,
  useEditorContext,
} from "../editor-context";
import { EditorFileDropArea } from "../editor-file-drop-area";
import { EditorMediaContainer } from "../editor-media-container";
import { getEditorMediaUploadField } from "../editor-media-target";
import { TheEditor } from "../the-editor";
import { EditorToolbar } from "../toolbar";

const TAB = {
  ...EDITOR_MEDIA_TAB,
  body: "body",
  gatedBody: "gatedBody",
  poll: "poll",
};

const TRANSIENT_EDITOR_TABS = new Set<string>([
  EDITOR_MEDIA_TAB.Embed,
  EDITOR_MEDIA_TAB.Emoji,
  EDITOR_MEDIA_TAB.Poll,
]);

type OverlayEditorTab = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
};

export const PostOverlayEditor = ({
  title = "New Post",
  comment = false,
}: {
  title?: string;
  comment?: boolean;
}) => {
  const {
    body,
    gatedBody,
    media,
    gatedMedia,
    poll,
    mode,
    gated,
    activeMediaTab,
    subscriptionTier,
    setEditorContext,
    lastFocusedField,
  } = useEditorContext();
  const [tab, setTab] = useState(TAB.body);
  const { user } = use(UserContext);
  const isGatedPost = gated();
  const wasGatedRef = useRef(isGatedPost);
  const bodyTabLabel = isGatedPost ? "Preview Content" : title;
  const showPollTab = !comment && (activeMediaTab === TAB.poll || !!poll);
  const showEmbedTab = activeMediaTab === TAB.Embed || tab === TAB.Embed;
  const showEmojiTab = activeMediaTab === TAB.Emoji || tab === TAB.Emoji;
  const mediaUploadField = getEditorMediaUploadField({
    activeTab: tab,
    isGated: isGatedPost,
    lastFocusedField,
  });
  const overlayTabs: OverlayEditorTab[] = [
    { icon: LuFileText, label: bodyTabLabel, value: TAB.body },
    ...(isGatedPost
      ? [{ icon: LuLock, label: "Gated Content", value: TAB.gatedBody }]
      : []),
    ...(showPollTab
      ? [{ icon: LuBarChartHorizontalBig, label: "Poll", value: TAB.poll }]
      : []),
    ...(showEmbedTab
      ? [{ icon: LuLink, label: "Embed", value: TAB.Embed }]
      : []),
    ...(showEmojiTab
      ? [{ icon: LuSmilePlus, label: "Emoji", value: TAB.Emoji }]
      : []),
  ];

  useEffect(() => {
    if (activeMediaTab) {
      setTab(activeMediaTab);
      return;
    }

    if (TRANSIENT_EDITOR_TABS.has(tab)) {
      setTab("body");
    }
  }, [activeMediaTab, tab]);

  useEffect(() => {
    const wasGated = wasGatedRef.current;
    wasGatedRef.current = isGatedPost;

    if (
      !comment &&
      !activeMediaTab &&
      !wasGated &&
      isGatedPost &&
      lastFocusedField === "gatedBody" &&
      tab === TAB.body
    ) {
      setTab(TAB.gatedBody);
    }
  }, [activeMediaTab, comment, isGatedPost, lastFocusedField, tab]);

  const handleTabChange = (nextTab: string | number | null) => {
    if (typeof nextTab !== "string") {
      return;
    }

    setTab(nextTab);
    setEditorContext({
      activeMediaTab: TRANSIENT_EDITOR_TABS.has(nextTab)
        ? (nextTab as EDITOR_MEDIA_TAB)
        : null,
    });
  };

  return (
    <Tabs
      value={tab}
      onValueChange={handleTabChange}
      defaultValue={TAB.body}
      className="flex size-full min-h-0 flex-col bg-background"
    >
      <TabsList
        className="
          unforce-radius z-20 flex w-full border-t-0 border-b
          border-border bg-background/95 p-2 pr-14 backdrop-blur-md
        "
      >
        <HorizontalScrollRail
          className="min-w-0 flex-1"
          contentClassName="gap-1 p-px"
          scrollStep={180}
        >
          {overlayTabs.map((item) => {
            const Icon = item.icon;

            return (
              <TabsTrigger
                key={item.value}
                value={item.value}
                className={cn(
                  `
                    inline-flex shrink-0 items-center gap-2 rounded-full
                    border border-transparent bg-background p-2 text-sm
                    text-muted transition-colors
                    hover:border-border hover:text-foreground
                    data-active:border-primary data-active:bg-primary/10
                    data-active:text-foreground
                  `,
                  tab === item.value &&
                    "border-primary bg-primary/10 text-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="max-w-[170px] truncate">{item.label}</span>
              </TabsTrigger>
            );
          })}
        </HorizontalScrollRail>
      </TabsList>

      <TabsContent
        value="body"
        className="flex size-full min-h-0 grow flex-col border-0! p-2"
      >
        <EditorFieldContextProvider
          name="body"
          placeholder="What's on your mind?"
        >
          <div
            className="
              h-0 w-full shrink-0 overflow-hidden opacity-0
              transition-[opacity,height] duration-500
              will-change-auto in-data-[mode=rich]:h-auto in-data-[mode=rich]:opacity-100
            "
          >
            <EditorToolbar />
          </div>
          <div className="min-h-0 grow overflow-y-auto">
            <TheEditor />
          </div>
          <EditorFieldContextProvider name="media">
            <EditorMediaContainer />
            <EditorFileDropArea
              targetLabel={
                comment
                  ? "this comment"
                  : isGatedPost
                    ? "preview content"
                    : "this post"
              }
            />
          </EditorFieldContextProvider>
        </EditorFieldContextProvider>
      </TabsContent>

      {!comment && (
        <TabsContent
          value={TAB.gatedBody}
          className="flex size-full min-h-0 grow flex-col p-2"
        >
          <EditorFieldContextProvider
            name="gatedBody"
            placeholder="Add your gated content here..."
          >
            <div
              className="
                h-0 overflow-hidden opacity-0 transition-[opacity,height]
                duration-500 will-change-auto
                in-data-[gated=true]:h-auto in-data-[gated=true]:opacity-100
              "
            >
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {subscriptionTier && (
                  <Badge
                    variant="outline"
                    className="inline-flex text-muted-foreground"
                  >
                    {subscriptionTier!.accessGroupKeyName.includes(
                      AccessGroupType.OneTime,
                    )
                      ? `Pay Once to Unlock - ${formatUSDCents(subscriptionTier.costUsdCents)}`
                      : `${subscriptionTier.name} - ${subscriptionTier.costUsdCents === 0 ? "Free" : `${formatUSDCents(subscriptionTier.costUsdCents)}/mo`}`}
                  </Badge>
                )}
                <EditorActionSwapGatedContent className="size-8 bg-background" />
              </div>
            </div>
            <div
              className="
                h-0 overflow-hidden opacity-0 transition-[opacity,height]
                duration-500 will-change-auto
                in-data-[mode=rich]:h-auto in-data-[mode=rich]:opacity-100
              "
            >
              <EditorToolbar />
            </div>
            <div className="min-h-0 grow overflow-y-auto">
              <TheEditor />
            </div>
            <EditorFieldContextProvider name="gatedMedia">
              <EditorMediaContainer />
              <EditorFileDropArea targetLabel="gated content" />
            </EditorFieldContextProvider>
          </EditorFieldContextProvider>
        </TabsContent>
      )}

      {!comment && (
        <TabsContent
          className="min-h-0 grow overflow-y-auto p-2 pb-3"
          value={TAB.poll}
        >
          <EditorFieldContextProvider name="poll">
            <EditorPollForm />
          </EditorFieldContextProvider>
        </TabsContent>
      )}

      <TabsContent
        className="min-h-0 grow overflow-y-auto p-2 pb-3"
        value={TAB.Embed}
      >
        <EditorFieldContextProvider name="poll">
          <EditorEmbedActionDetails />
        </EditorFieldContextProvider>
      </TabsContent>

      <TabsContent
        className="
          min-h-0 grow p-2 pb-3
          [&>div]:h-full
          [&>div>aside]:h-full!
        "
        value={TAB.Emoji}
      >
        <EditorFieldContextProvider name={lastFocusedField || "body"}>
          <EditorEmojiPicker />
        </EditorFieldContextProvider>
      </TabsContent>

      <div className="unforce-radius flex p-2">
        <div className="grow">
          <EditorFooterActionContainer>
            <EditorFooterActionButton
              action={EditorActionFormatting}
              tooltipText="Formatting"
              active={mode === EDITOR_MODE.RICH}
            />
            <EditorFieldContextProvider name={mediaUploadField}>
              <EditorFooterActionButton
                action={EditorActionPhoto}
                tooltipText="Upload Images"
                data-auto-action="photo"
              />
            </EditorFieldContextProvider>
            <EditorFieldContextProvider name={mediaUploadField}>
              <EditorFooterActionButton
                action={EditorActionVideo}
                tooltipText="Upload Videos"
                data-auto-action="video"
              />
            </EditorFieldContextProvider>
            <EditorFieldContextProvider name={mediaUploadField}>
              <EditorFooterActionButton
                action={EditorActionAudio}
                tooltipText="Upload Audio"
                side="top"
              />
            </EditorFieldContextProvider>
            {!comment && (
              <EditorFooterActionButton
                action={EditorActionPoll}
                tooltipText="Add Poll"
                active={activeMediaTab === EDITOR_MEDIA_TAB.Poll}
                data-auto-action="poll"
              />
            )}
            <EditorFieldContextProvider name={lastFocusedField || "body"}>
              <EditorFooterActionButton
                action={EditorActionGif}
                tooltipText="Add GIF"
              />
            </EditorFieldContextProvider>
            <EditorFooterActionButton
              action={EditorActionEmbed}
              tooltipText="Manage embedded media"
              active={activeMediaTab === EDITOR_MEDIA_TAB.Embed}
            />
            <EditorFieldContextProvider name={lastFocusedField || "body"}>
              <EditorFooterActionButton
                action={EditorActionEmoji}
                tooltipText="Add emoji"
                active={activeMediaTab === EDITOR_MEDIA_TAB.Emoji}
                className="
                  hidden
                  md:inline-flex
                "
              />
            </EditorFieldContextProvider>
          </EditorFooterActionContainer>
        </div>

        <div className="ml-auto flex gap-2">
          {!comment && (
            <>
              <div>
                <EditorActionDraft />
              </div>
              <div>
                <EditorActionGated />
              </div>
            </>
          )}
          <div>
            <EditorActionSubmit
              className="px-3"
              onClick={() => {
                if (!comment) {
                  saveDraft(
                    user!.publicKey,
                    {
                      body,
                      gatedBody,
                      media,
                      gatedMedia,
                      poll,
                    },
                    subscriptionTier,
                    true,
                  );
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="absolute size-px overflow-hidden" aria-hidden="true">
        <EditorFieldContextProvider name={mediaUploadField}>
          <EditorActionAudio
            defaultAction="file"
            data-auto-action="audio"
            tabIndex={-1}
          />
        </EditorFieldContextProvider>
        <EditorFieldContextProvider name={mediaUploadField}>
          <EditorActionAudio
            defaultAction="record"
            data-auto-action="record"
            tabIndex={-1}
          />
        </EditorFieldContextProvider>
      </div>
    </Tabs>
  );
};
